import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { UserStats } from "../components/UserStats";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, Coins, KeyRound, Trash2 } from "lucide-react";

export default function AdminUserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, translateError } = useLanguage();
  const [user, setUser] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const [creditsOpen, setCreditsOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [savingCredits, setSavingCredits] = useState(false);

  const [pwOpen, setPwOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const load = () => {
    api.get(`/admin/users/${id}`)
      .then(({ data }) => setUser(data))
      .catch(() => setNotFound(true));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const addCredits = async () => {
    const n = parseInt(amount, 10);
    if (!n || n < 1) return toast.error(t("adminUser.invalidAmount"));
    setSavingCredits(true);
    try {
      const { data } = await api.post(`/admin/users/${id}/credits`, { amount: n });
      setUser((u) => ({ ...u, credits: data.credits }));
      setCreditsOpen(false);
      setAmount("");
      toast.success(t("adminUser.addCreditsSaved"));
    } catch (e) {
      toast.error(translateError(formatApiError(e.response?.data?.detail)));
    } finally {
      setSavingCredits(false);
    }
  };

  const setNewPassword = async () => {
    if (!password || password.length < 6) return toast.error(t("adminUser.newPassword"));
    setSavingPw(true);
    try {
      await api.post(`/admin/users/${id}/password`, { new_password: password });
      setPwOpen(false);
      setPassword("");
      toast.success(t("adminUser.setPasswordSaved"));
    } catch (e) {
      toast.error(translateError(formatApiError(e.response?.data?.detail)));
    } finally {
      setSavingPw(false);
    }
  };

  const deleteUser = async () => {
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success(t("adminUser.deleted"));
      navigate("/admin/users");
    } catch (e) {
      toast.error(translateError(formatApiError(e.response?.data?.detail)));
    }
  };

  if (notFound) {
    return (
      <main className="relative z-10 mx-auto max-w-2xl px-6 py-24 text-center">
        <h1 className="font-heading text-3xl font-black">{t("adminUser.notFound")}</h1>
        <Link to="/admin/users" className="mt-6 inline-block text-primary hover:underline">{t("adminUser.back")}</Link>
      </main>
    );
  }
  if (!user) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const joined = user.created_at ? new Date(user.created_at).toLocaleDateString() : "—";

  return (
    <main className="relative z-10 mx-auto max-w-3xl px-6 py-14 md:px-10">
      <Link to="/admin/users" data-testid="back-to-users" className="text-sm uppercase tracking-widest text-muted-foreground hover:text-foreground">{t("adminUser.back")}</Link>

      <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="font-mono text-xs uppercase tracking-widest text-primary">{t("adminUser.account")}</span>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter">{user.name || "—"}</h1>
          <p className="mt-1 text-muted-foreground">{user.email}</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => setCreditsOpen(true)} data-testid="open-add-credits" className="rounded-md bg-credit font-bold uppercase tracking-wider text-black hover:bg-credit/80">
            <Coins className="mr-2 h-4 w-4" /> {t("adminUser.addCredits")}
          </Button>
          <Button onClick={() => setPwOpen(true)} data-testid="open-set-password" variant="outline" className="rounded-md border-white/20 bg-transparent font-bold uppercase tracking-wider hover:bg-white/10">
            <KeyRound className="mr-2 h-4 w-4" /> {t("adminUser.setPassword")}
          </Button>
          {user.role !== "admin" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="open-delete-user" variant="outline" className="rounded-md border-lie/40 bg-transparent font-bold uppercase tracking-wider text-lie hover:bg-lie/10">
                  <Trash2 className="mr-2 h-4 w-4" /> {t("adminUser.delete")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="border-white/10 bg-card" data-testid="delete-user-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("adminUser.deleteTitle")}</AlertDialogTitle>
                  <AlertDialogDescription>{t("adminUser.deleteDesc", { name: user.name || user.email })}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="border-white/15 bg-transparent">{t("common.cancel")}</AlertDialogCancel>
                  <AlertDialogAction onClick={deleteUser} data-testid="confirm-delete-user" className="bg-lie hover:bg-lie/80">{t("adminUser.delete")}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <dl className="mt-6 grid grid-cols-2 gap-px overflow-hidden border border-white/10 bg-white/10">
        <div className="bg-card p-4">
          <dt className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t("adminUser.role")}</dt>
          <dd className="mt-0.5 text-sm text-foreground/90">{user.role}</dd>
        </div>
        <div className="bg-card p-4">
          <dt className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{t("adminUser.joined")}</dt>
          <dd className="mt-0.5 text-sm text-foreground/90">{joined}</dd>
        </div>
      </dl>

      <h2 className="mt-10 font-heading text-xl font-bold">{t("stats.title")}</h2>
      <div className="mt-4">
        <UserStats stats={user} />
      </div>

      <Dialog open={creditsOpen} onOpenChange={setCreditsOpen}>
        <DialogContent className="max-w-md border-white/10 bg-card" data-testid="add-credits-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black">{t("adminUser.addCreditsTitle")}</DialogTitle>
            <DialogDescription>{t("adminUser.addCreditsDesc", { name: user.name || user.email })}</DialogDescription>
          </DialogHeader>
          <div>
            <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("adminUser.amountLabel")}</label>
            <Input type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="credits-amount-input" className="border-white/15 bg-background" placeholder="50" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditsOpen(false)} className="border-white/15 bg-transparent">{t("common.cancel")}</Button>
            <Button onClick={addCredits} disabled={savingCredits} data-testid="confirm-add-credits" className="rounded-md bg-credit font-bold uppercase tracking-wider text-black hover:bg-credit/80">
              {savingCredits ? <Loader2 className="h-4 w-4 animate-spin" /> : t("adminUser.addCredits")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="max-w-md border-white/10 bg-card" data-testid="set-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black">{t("adminUser.setPasswordTitle")}</DialogTitle>
            <DialogDescription>{t("adminUser.setPasswordDesc", { name: user.name || user.email })}</DialogDescription>
          </DialogHeader>
          <div>
            <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("adminUser.newPassword")}</label>
            <Input type="text" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="new-password-input" className="border-white/15 bg-background" placeholder="••••••••" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)} className="border-white/15 bg-transparent">{t("common.cancel")}</Button>
            <Button onClick={setNewPassword} disabled={savingPw} data-testid="confirm-set-password" className="rounded-md bg-primary font-bold uppercase tracking-wider hover:bg-[#FF645A]">
              {savingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : t("adminUser.setPassword")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
